'use client'

import * as React from 'react'
import type { Img } from '../../types/props.js'
import { Picture, Text } from '../../components/index.js'
import { cn } from '../../helpers/index.js'

export interface CircleProgressProps {
  /** 总阶段数 (1-4) */
  totalSteps?: 1 | 2 | 3 | 4
  /** 当前完成的阶段 (0 到 totalSteps) */
  currentStep?: number
  /** 中间显示的图片 */
  image?: Img
  /** 底部显示的文案 */
  label?: string
  /** 进度条颜色，默认为品牌色 */
  progressColor?: string
  /** 底部文案背景色，默认为品牌营销色 */
  labelColor?: string
  /** 未完成阶段的颜色 */
  trackColor?: string
  /** 背景颜色，默认为容器背景色 */
  backgroundColor?: string
  /** 移动端组件尺寸，默认 64 */
  size?: number
  /** laptop 以上组件尺寸，默认 80 */
  laptopSize?: number
  /** 段之间的间隙（像素），默认 3 */
  gap?: number
  /** 自定义类名 */
  className?: string
}

/**
 * 分段式圆形进度条组件
 * 用于 MiniCart 左侧显示阶段性进度，支持 1-4 个阶段配置
 * 每个阶段是独立的弧段，中间有间隙
 * 移动端默认 64px，laptop 以上默认 80px
 */
const CircleProgress = ({
  totalSteps = 4,
  currentStep = 0,
  image,
  label,
  progressColor = 'var(--brand-color-0)',
  labelColor = 'var(--marketing-color-1)',
  trackColor = '#DADCE0',
  backgroundColor = '#EAEAEC',
  size = 64,
  laptopSize = 96,
  gap = 3,
  className,
}: CircleProgressProps) => {
  // 使用较大尺寸计算 SVG 参数，确保在所有尺寸下都清晰
  const maxSize = Math.max(size, laptopSize) // 如果有标签，预留底部空间
  const strokeWidth = 6
  const radius = (maxSize - strokeWidth) / 2
  const center = maxSize / 2

  // 生成所有分段
  const segments = React.useMemo(() => {
    // 视觉间隙需要加上两端圆角帽子的宽度（strokeLinecap="round" 会延伸 strokeWidth/2）
    const actualGap = gap + strokeWidth
    // 将像素间隙转换为角度（弧度 = 弧长 / 半径，再转为度）
    const gapAngle = (actualGap / radius) * (180 / Math.PI)
    const totalGapAngle = gapAngle * totalSteps
    const availableAngle = 360 - totalGapAngle
    const segmentAngle = availableAngle / totalSteps

    // 将角度转换为弧度
    const toRadians = (degrees: number) => (degrees * Math.PI) / 180

    // 计算弧段的起点和终点坐标
    const getArcPath = (startAngle: number, endAngle: number) => {
      // 从顶部开始（-90度），顺时针方向
      const adjustedStart = startAngle - 90
      const adjustedEnd = endAngle - 90

      const startX = center + radius * Math.cos(toRadians(adjustedStart))
      const startY = center + radius * Math.sin(toRadians(adjustedStart))
      const endX = center + radius * Math.cos(toRadians(adjustedEnd))
      const endY = center + radius * Math.sin(toRadians(adjustedEnd))

      // 判断是否为大弧（超过180度）
      const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0

      return `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`
    }

    const result = []
    // totalSteps = 2 时，起始位置逆时针偏移 45 度，让断开位置在左上和右下
    const startOffset = totalSteps === 2 ? -45 : 0
    let currentAngle = startOffset

    for (let i = 0; i < totalSteps; i++) {
      const startAngle = currentAngle + gapAngle / 2
      const endAngle = startAngle + segmentAngle
      const isCompleted = i < currentStep

      result.push({
        path: getArcPath(startAngle, endAngle),
        isCompleted,
        index: i,
      })

      currentAngle = endAngle + gapAngle / 2
    }

    return result
  }, [totalSteps, currentStep, gap, radius, center])

  return (
    <div
      className={cn(
        'relative inline-flex shrink-0 flex-col items-center',
        'laptop:w-[--circle-laptop-size] w-[--circle-size]',
        'laptop:h-[--circle-laptop-height] h-[--circle-height]',
        className
      )}
      style={
        {
          '--circle-size': `${size}px`,
          '--circle-laptop-size': `${laptopSize}px`,
          '--circle-height': `${label ? size + 8 : size}px`,
          '--circle-laptop-height': `${label ? laptopSize + 8 : laptopSize}px`,
        } as React.CSSProperties
      }
    >
      {/* 进度条圆环 */}
      <div className="laptop:size-[--circle-laptop-size] relative size-[--circle-size]">
        <svg
          viewBox={`0 0 ${maxSize} ${maxSize}`}
          className={cn('absolute inset-0 size-full', label ? 'translate-y-[-8px]' : '')}
        >
          {/* 背景圆形 */}
          <circle cx={center} cy={center} r={radius} fill={backgroundColor} stroke="none" />

          {/* 分段弧 */}
          {segments.map(segment => (
            <path
              key={segment.index}
              d={segment.path}
              fill="none"
              stroke={segment.isCompleted ? progressColor : trackColor}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              style={{
                transition: 'stroke 0.3s ease-in-out',
              }}
            />
          ))}
        </svg>

        {/* 中间内容区域 */}
        <div
          className={cn('absolute inset-[10px] flex items-center justify-center', label ? 'translate-y-[-8px]' : '')}
        >
          <div
            className="flex size-full items-center justify-center overflow-hidden rounded-full"
            style={{ backgroundColor }}
          >
            {image?.url && (
              <Picture className="size-full" imgClassName="object-contain" source={image.url} alt={image.alt || ''} />
            )}
          </div>
        </div>
      </div>

      {/* 底部文案 - 和圆形底部重叠 */}
      {label && (
        <div
          className="absolute bottom-0 left-1/2 flex -translate-x-1/2 items-center justify-center rounded-full px-2 py-0.5"
          style={{ backgroundColor: labelColor }}
        >
          <Text className="text-xs font-medium text-white" html={label} />
        </div>
      )}
    </div>
  )
}

CircleProgress.displayName = 'CircleProgress'

export default CircleProgress
